import React, { useState, useEffect, useRef } from 'react';
import { X } from 'lucide-react';

export type TooltipPosition = 'top' | 'bottom' | 'left' | 'right' | 'auto';
export type TooltipTrigger = 'hover' | 'click' | 'both';

interface TooltipProps {
  children: React.ReactNode;
  content: React.ReactNode;
  position?: TooltipPosition;
  trigger?: TooltipTrigger;
  maxWidth?: string;
  maxHeight?: string;
  showCloseButton?: boolean;
  disabled?: boolean;
  className?: string;
  tooltipClassName?: string;
  offset?: number;
  arrow?: boolean;
}

export const Tooltip: React.FC<TooltipProps> = ({
  children,
  content,
  position = 'auto',
  trigger = 'hover',
  maxWidth = '20rem',
  maxHeight = '24rem',
  showCloseButton = true,
  disabled = false,
  className = '',
  tooltipClassName = '',
  offset = 8,
  arrow = true,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [computedPosition, setComputedPosition] = useState<'top' | 'bottom' | 'left' | 'right'>('top');
  const triggerRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Calculate optimal position
  useEffect(() => {
    if (!isOpen || !triggerRef.current || !tooltipRef.current || position !== 'auto') {
      if (position !== 'auto') {
        setComputedPosition(position);
      }
      return;
    }

    const triggerRect = triggerRef.current.getBoundingClientRect();
    const tooltipRect = tooltipRef.current.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    // Check available space in each direction
    const spaceTop = triggerRect.top;
    const spaceBottom = viewportHeight - triggerRect.bottom;
    const spaceLeft = triggerRect.left;
    const spaceRight = viewportWidth - triggerRect.right;

    // Determine best position
    if (spaceTop > tooltipRect.height + offset && spaceTop > spaceBottom) {
      setComputedPosition('top');
    } else if (spaceBottom > tooltipRect.height + offset) {
      setComputedPosition('bottom');
    } else if (spaceRight > tooltipRect.width + offset && spaceRight > spaceLeft) {
      setComputedPosition('right');
    } else if (spaceLeft > tooltipRect.width + offset) {
      setComputedPosition('left');
    } else {
      // Default to bottom if no good space
      setComputedPosition('bottom');
    }
  }, [isOpen, position, offset]);

  const handleOpen = () => {
    if (disabled) return;
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setIsOpen(true);
  };

  const handleClose = () => {
    if (trigger === 'hover') {
      // Delay closing to allow mouse to move to tooltip
      timeoutRef.current = setTimeout(() => {
        setIsOpen(false);
      }, 150);
    } else {
      setIsOpen(false);
    }
  };

  const handleTooltipEnter = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
  };

  const handleTooltipLeave = () => {
    if (trigger === 'hover' || trigger === 'both') {
      handleClose();
    }
  };

  const handleClick = (e: React.MouseEvent) => {
    if (trigger === 'click' || trigger === 'both') {
      e.stopPropagation();
      setIsOpen(!isOpen);
    }
  };

  const handleCloseClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsOpen(false);
  };

  // Close on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        setIsOpen(false);
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen]);

  // Close on click outside
  useEffect(() => {
    if (!isOpen || trigger === 'hover') return;

    const handleClickOutside = (e: MouseEvent) => {
      if (
        triggerRef.current &&
        !triggerRef.current.contains(e.target as Node) &&
        tooltipRef.current &&
        !tooltipRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, trigger]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  const getTooltipPositionClasses = () => {
    const baseClasses = 'absolute z-50';
    switch (computedPosition) {
      case 'top':
        return `${baseClasses} bottom-full left-1/2 -translate-x-1/2 mb-${offset}`;
      case 'bottom':
        return `${baseClasses} top-full left-1/2 -translate-x-1/2 mt-${offset}`;
      case 'left':
        return `${baseClasses} right-full top-1/2 -translate-y-1/2 mr-${offset}`;
      case 'right':
        return `${baseClasses} left-full top-1/2 -translate-y-1/2 ml-${offset}`;
      default:
        return `${baseClasses} bottom-full left-1/2 -translate-x-1/2 mb-${offset}`;
    }
  };

  const getArrowClasses = () => {
    if (!arrow) return '';

    const arrowBase = 'absolute w-3 h-3 bg-white border transform rotate-45';
    switch (computedPosition) {
      case 'top':
        return `${arrowBase} -bottom-1.5 left-1/2 -translate-x-1/2 border-t-0 border-l-0 border-gray-200`;
      case 'bottom':
        return `${arrowBase} -top-1.5 left-1/2 -translate-x-1/2 border-b-0 border-r-0 border-gray-200`;
      case 'left':
        return `${arrowBase} -right-1.5 top-1/2 -translate-y-1/2 border-t-0 border-r-0 border-gray-200`;
      case 'right':
        return `${arrowBase} -left-1.5 top-1/2 -translate-y-1/2 border-b-0 border-l-0 border-gray-200`;
      default:
        return '';
    }
  };

  return (
    <div className={`relative inline-block ${className}`} ref={triggerRef}>
      <div
        onMouseEnter={trigger === 'hover' || trigger === 'both' ? handleOpen : undefined}
        onMouseLeave={trigger === 'hover' || trigger === 'both' ? handleClose : undefined}
        onClick={handleClick}
        className="cursor-pointer"
      >
        {children}
      </div>

      {isOpen && !disabled && (
        <div
          ref={tooltipRef}
          className={getTooltipPositionClasses()}
          onMouseEnter={handleTooltipEnter}
          onMouseLeave={handleTooltipLeave}
          style={{ maxWidth, maxHeight }}
        >
          {/* Arrow */}
          {arrow && <div className={getArrowClasses()} />}

          {/* Tooltip content */}
          <div
            className={`
              bg-white rounded-xl border border-gray-200 shadow-2xl
              overflow-hidden animate-in fade-in zoom-in-95 duration-200
              ${tooltipClassName}
            `}
          >
            {/* Close button */}
            {showCloseButton && (trigger === 'click' || trigger === 'both') && (
              <div className="absolute top-2 right-2 z-10">
                <button
                  onClick={handleCloseClick}
                  className="p-1 rounded-md hover:bg-gray-100 transition-colors text-gray-500 hover:text-gray-700"
                  aria-label="Close tooltip"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}

            {/* Content */}
            <div className="relative">{content}</div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Tooltip;
